import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { Clock, ThumbsUp, ThumbsDown } from "lucide-react-native";
import { MoonFilterChip, MoonFormField } from "@louise/moon-ui";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useSexualHealth } from "../SexualHealthContext";
import { buildSexualHealthNavigationStack } from "../buildSexualHealthNavigationStack";
import { FREQUENCY_OPTIONS } from "../options";
import type { SexualHealthStackParamList } from "../SexualHealthNavigator";

export function PartnerEjaculateScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useSexualHealth();
  const navigation =
    useNavigation<NavigationProp<SexualHealthStackParamList>>();

  const selection = useMemo(() => {
    if (state.isPartnerEjaculatingInVagina == null) return null;
    return state.isPartnerEjaculatingInVagina ? "yes" : "no";
  }, [state.isPartnerEjaculatingInVagina]);

  const handleChange = (value: "yes" | "no") => {
    if (value === "no") {
      updateState({
        isPartnerEjaculatingInVagina: false,
        vaginalEjaculationFrequency: null,
      });
    } else {
      updateState({ isPartnerEjaculatingInVagina: true });
    }
  };

  const handleSave = async () => {
    await save();
    const stack = buildSexualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("PartnerEjaculate");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t(
        state.userGender === "male"
          ? "med_history.gynae.sexual_health.partner_ejaculate_title_amab"
          : "med_history.gynae.sexual_health.partner_ejaculate_title_afab",
      )}
      subtitle={t("med_history.gynae.sexual_health.partner_ejaculate_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selection === null}
      saveLabel={t("med_history.gynae.sexual_health.continue")}
    >
      <View style={styles.container}>
        <View style={styles.yesNoRow}>
          <SquareOptionButton
            icon={ThumbsUp}
            label={t("med_history.shared.yes")}
            isSelected={selection === "yes"}
            onPress={() => handleChange("yes")}
            iconSize={24}
            containerSize={48}
            labelSize="lg"
          />
          <SquareOptionButton
            icon={ThumbsDown}
            label={t("med_history.shared.no")}
            isSelected={selection === "no"}
            onPress={() => handleChange("no")}
            iconSize={24}
            containerSize={48}
            labelSize="lg"
          />
        </View>

        {selection === "yes" && (
          <MoonFormField
            label={t(
              "med_history.gynae.sexual_health.partner_ejaculate_frequency_label",
            )}
            labelIcon={Clock}
            style={styles.followUp}
          >
            <View style={styles.frequencyRow}>
              {FREQUENCY_OPTIONS.map((option) => (
                <MoonFilterChip
                  key={option.id}
                  label={t(option.labelKey)}
                  selected={state.vaginalEjaculationFrequency === option.id}
                  onPress={() =>
                    updateState({
                      vaginalEjaculationFrequency: option.id,
                    })
                  }
                  tone="glass"
                  showCheck={false}
                  testID={`partner-ejaculate-frequency-${option.id}`}
                />
              ))}
            </View>
          </MoonFormField>
        )}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  yesNoRow: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
    marginBottom: 24,
  },
  followUp: {
    width: "100%",
  },
  frequencyRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
});
